// Pure-CSS charts for the admin Overview screen.
import { cn } from '../../../lib/cn';
import type { AdminTrendPoint } from '../../../lib/adminApi';

/** Thin horizontal fill bar (0..1). Green when healthy, amber/red when low. */
export function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  const tone = value >= 0.66 ? 'bg-accent' : value >= 0.33 ? 'bg-gold-bottom' : 'bg-miss';
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-fill">
      <div className={cn('h-full rounded-full transition-all', tone)} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Simple two-series weekly bar chart (signups + picks), pure CSS, no deps. */
export function TrendChart({ data }: { data: AdminTrendPoint[] }) {
  const max = Math.max(1, ...data.map((d) => Math.max(d.signups, d.picks)));
  return (
    <div>
      <div className="mb-3 flex items-center gap-4 px-1">
        <Legend className="bg-night" label="Signups" />
        <Legend className="bg-accent" label="Picks" />
      </div>
      <div className="flex items-end gap-2" style={{ height: 132 }}>
        {data.map((d) => (
          <div key={d.weekStart} className="flex flex-1 flex-col items-center gap-1.5">
            <div className="flex h-full w-full items-end justify-center gap-1">
              <Bar value={d.signups} max={max} className="bg-night" title={`${d.signups} signups`} />
              <Bar value={d.picks} max={max} className="bg-accent" title={`${d.picks} picks`} />
            </div>
            <span className="text-[10px] text-ink3">{weekTick(d.weekStart)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bar({ value, max, className, title }: { value: number; max: number; className: string; title: string }) {
  const h = max > 0 ? (value / max) * 100 : 0;
  return (
    <div title={title} className="flex h-full w-3 items-end md:w-4">
      <div className={cn('w-full rounded-t-[3px]', className)} style={{ height: `${Math.max(value > 0 ? 4 : 0, h)}%` }} />
    </div>
  );
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[11px] font-semibold text-ink2">
      <span className={cn('h-2.5 w-2.5 rounded-sm', className)} /> {label}
    </span>
  );
}

/** Short tick for a week start, e.g. "9 Jun". */
function weekTick(ms: number): string {
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
